import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { XCircle, Bot, Book, Settings, Loader2, MessageSquare, Check, Sparkles } from "lucide-react";

// Custom components (adjust paths based on your project structure)
import Switch from "../Switch";
import AiChatbotKnowledgeBaseField from "./AiChatbotKnowledgeBaseField";
import AiChatbotEditableField from "./AiChatbotEditableField";
import AiChatbotScheduleField from "./AiChatbotScheduleField";
import AiChatbotButtonInputs from "./AiChatbotButtonInputs";
import { getUserCredits } from "../../apis/subscription";
import CreditPurchaseModal from "../CreditPurchaseModal";

const AiChatbotModal = ({
  onClose,
  loading,
  phoneOptions,
  selectedPhoneId,
  onSelectPhone,
  aiEnabled,
  onToggleAi,
  toggleLoading,
  knowledgeDraft,
  onKnowledgeChange,
  toneDraft,
  onToneChange,
  outOfContextDraft,
  onOutOfContextChange,
  languageDraft,
  onLanguageChange,
  roleDraft,
  onRoleChange,
  tasksAndRulesDraft,
  onTasksAndRulesChange,
  aiReplyDays,
  aiReplyStartTime,
  aiReplyEndTime,
  aiTimezone,
  onScheduleChange,
  aiReplyToButtons,
  onAiReplyToButtonsChange,
  aiReplyToAll,
  onAiReplyToAllChange,
  aiButtonTexts,
  onAiButtonTextsChange,
  onSaveAll,
  saving,
}) => {
  const selectedPhone = phoneOptions.find(
    (option) => option.phone_id === selectedPhoneId
  );

  const handleSaveTone = async (value) => {
    onToneChange(value);
    await onSaveAll({ tone: value });
  };

  const handleSaveOutOfContext = async (value) => {
    onOutOfContextChange(value);
    await onSaveAll({ out_of_context_reply: value });
  };

  const handleSaveKnowledge = async (value) => {
    onKnowledgeChange({ target: { value } });
    await onSaveAll({ knowledge_base: value });
  };

  const handleSaveLanguage = async (value) => {
    onLanguageChange(value);
    await onSaveAll({ language: value });
  };

  const handleSaveRole = async (value) => {
    onRoleChange(value);
    await onSaveAll({ role: value });
  };

  const handleSaveTasksAndRules = async (value) => {
    onTasksAndRulesChange(value);
    await onSaveAll({ tasks_and_rules: value });
  };

  const handleSaveAiReplyToButtons = async (value) => {
    onAiReplyToButtonsChange(value);
    await onSaveAll({ ai_reply_to_buttons: value });
  };

  const handleSaveAiReplyToAll = async (value) => {
    onAiReplyToAllChange(value);
    await onSaveAll({ ai_reply_to_all: value });
  };

  const [userCredits, setUserCredits] = useState(null);
  const [showCreditModal, setShowCreditModal] = useState(false);

  // Ensure aiButtonTexts is always an array
  const buttonTextsArray = Array.isArray(aiButtonTexts) 
    ? aiButtonTexts 
    : (aiButtonTexts ? (typeof aiButtonTexts === 'string' ? aiButtonTexts.split(',').map(t => t.trim()).filter(t => t) : [aiButtonTexts]) : []);

  // Load user credits
  useEffect(() => {
    loadUserCredits();
  }, []);

  const loadUserCredits = async () => {
    try {
      const response = await getUserCredits();
      if (response.data.success) {
        setUserCredits(response.data.data);
      }
    } catch (error) {
      console.error("Error loading user credits:", error);
    }
  };

  const handleSaveAiButtonTexts = async (buttonsArray) => {
    try {
      await onSaveAll({ ai_button_texts: buttonsArray });
      onAiButtonTextsChange(buttonsArray);
    } catch (error) {
      console.error("Error saving button texts:", error);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-8 overflow-y-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          className="bg-white rounded-lg shadow-2xl max-w-4xl w-full p-4 relative my-4 max-h-[90vh] overflow-y-auto"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-800 z-10"
          >
            <XCircle size={20} />
          </button>

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-full bg-[#01CC54]/10 text-[#01CC54]">
                <Bot size={18} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  AI Chatbot Configuration
                </h2>
                <p className="text-xs text-gray-600">
                  Configure AI chatbot settings per WhatsApp number
                </p>
              </div>
            </div>
            {userCredits && (
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-r from-[#048AAD]/10 to-[#01CC54]/10 border border-[#048AAD]/20 rounded-lg px-4 py-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#048AAD]" />
                    <div className="text-sm">
                      <div className="font-bold text-gray-900">
                        {userCredits.totalAvailableReplies} AI replies
                      </div>
                      {userCredits.batches?.[0]?.creditPlan && (
                        <div className="text-xs text-gray-600">
                          {userCredits.totalCredits} credits (1 credit = {userCredits.batches[0].creditPlan.per_credit_ai_replies} replies)
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowCreditModal(true)}
                  className="px-3 py-2 bg-gradient-to-r from-[#048AAD] to-[#01CC54] text-white rounded-lg font-semibold text-sm hover:opacity-90 transition-opacity flex items-center gap-2"
                >
                  <Sparkles size={14} />
                  Buy Credits
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Loader2 size={28} className="animate-spin mb-3" />
              <p className="text-sm">Loading connected numbers...</p>
            </div>
          ) : phoneOptions.length === 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
              <p className="text-sm font-semibold mb-1">
                No WhatsApp numbers connected yet.
              </p>
              <p className="text-xs">
                Connect a WhatsApp business number first to enable AI
                automation.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Phone Selection */}
              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <label className="block text-xs font-semibold text-gray-900 mb-1.5">
                  Select WhatsApp Number
                </label>
                <select
                  value={selectedPhoneId}
                  onChange={(e) => onSelectPhone(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 bg-white focus:ring-2 focus:ring-[#01CC54]/40 focus:border-[#01CC54] transition"
                >
                  {phoneOptions.map((option) => (
                    <option key={option.phone_id} value={option.phone_id}>
                      {option.phone_number || option.phone_id}{" "}
                      {option.verified_name ? `• ${option.verified_name}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              {selectedPhone && (
                <>
                  {/* AI Toggle */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-200">
                    <div className="flex items-center space-x-2">
                      <Bot size={18} className="text-[#01CC54]" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          Enable AI Chatbot
                        </div>
                        <div className="text-xs text-gray-600">
                          Allow the chatbot to automatically reply using company
                          data
                        </div>
                      </div>
                    </div>
                    <Switch
                      enabled={aiEnabled}
                      onChange={(v) => onToggleAi(v)}
                    />
                  </div>

                  {/* Knowledge Base - Always visible so users can add it before enabling AI */}
                  <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                    <div className="flex items-start space-x-2">
                      <Book size={18} className="text-[#01CC54] mt-0.5" />
                      <div className="flex-1">
                        <AiChatbotKnowledgeBaseField
                          label="Company Knowledge Base"
                          value={knowledgeDraft}
                          onChange={onKnowledgeChange}
                          onSave={handleSaveKnowledge}
                          placeholder="Enter company FAQs, policies, or knowledge base..."
                          helpText="Add your company FAQs, policies, or reference notes for chatbot use. This is required to enable AI chatbot."
                        />
                      </div>
                    </div>
                  </div>


                  {aiEnabled && (
                    <>
                      {/* Settings Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Language */}
                        <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 flex flex-col">
                          <div className="flex items-center mb-3">
                            <Settings
                              size={18}
                              className="mr-2 text-[#01CC54]"
                            />
                            <h4 className="text-sm font-semibold text-gray-900">
                              Language Settings
                            </h4>
                          </div>
                          <AiChatbotEditableField
                            label="Supported Languages"
                            value={
                              Array.isArray(languageDraft)
                                ? languageDraft.join(", ")
                                : languageDraft || ""
                            }
                            onChange={(e) => onLanguageChange(e.target.value)}
                            onSave={handleSaveLanguage}
                            placeholder="e.g., English, Spanish, French, Hindi"
                            helpText="Specify which languages your chatbot can communicate in. AI will respond in the same language the user uses if it's in this list."
                          />
                        </div>

                        {/* Role */}
                        <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 flex flex-col">
                          <div className="flex items-center mb-3">
                            <Settings
                              size={18}
                              className="mr-2 text-[#01CC54]"
                            />
                            <h4 className="text-sm font-semibold text-gray-900">
                              Role Settings
                            </h4>
                          </div>
                          <AiChatbotEditableField
                            label="Role Description"
                            value={roleDraft}
                            onChange={(e) => onRoleChange(e.target.value)}
                            onSave={handleSaveRole}
                            rows={4}
                            placeholder="Enter role description... (e.g., support staff, developer, sales agent)"
                            helpText="Provide a description of the AI role or persona (e.g., customer support, developer, sales agent)"
                          />
                        </div>

                        {/* Tasks and Rules */}
                        <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 flex flex-col">
                          <div className="flex items-center mb-3">
                            <Settings
                              size={18}
                              className="mr-2 text-[#01CC54]"
                            />
                            <h4 className="text-sm font-semibold text-gray-900">
                              Tasks and Rules
                            </h4>
                          </div>
                          <AiChatbotEditableField
                            label="Tasks and Rules"
                            value={tasksAndRulesDraft}
                            onChange={(e) =>
                              onTasksAndRulesChange(e.target.value)
                            }
                            onSave={handleSaveTasksAndRules}
                            rows={8}
                            placeholder="Enter AI tasks and rules for handling conversations..."
                            helpText="Define specific tasks, rules, or guidelines for the AI chatbot to follow during conversations"
                          />
                        </div>

                        {/* Tone */}
                        <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 flex flex-col">
                          <div className="flex items-center mb-3">
                            <Settings
                              size={18}
                              className="mr-2 text-[#01CC54]"
                            />
                            <h4 className="text-sm font-semibold text-gray-900">
                              Tone Settings
                            </h4>
                          </div>
                          <AiChatbotEditableField
                            label="Tone Description"
                            value={toneDraft}
                            onChange={(e) => onToneChange(e.target.value)}
                            onSave={handleSaveTone}
                            rows={4}
                            placeholder="Enter tone description..."
                            helpText="Describe the tone or style for chatbot replies"
                          />
                        </div>

                        {/* Out of Context */}
                        <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 flex flex-col">
                          <div className="flex items-center mb-3">
                            <Settings
                              size={18}
                              className="mr-2 text-[#01CC54]"
                            />
                            <h4 className="text-sm font-semibold text-gray-900">
                              Out of Context Reply
                            </h4>
                          </div>
                          <AiChatbotEditableField
                            label="Out of Context Reply Text"
                            value={outOfContextDraft}
                            onChange={(e) =>
                              onOutOfContextChange(e.target.value)
                            }
                            onSave={handleSaveOutOfContext}
                            rows={4}
                            placeholder="Enter reply text for out of context messages..."
                            helpText="Response text when chatbot receives out of context messages"
                          />
                        </div>
                      </div>

                      {/* Button Reply Settings */}
                      <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                        <div className="flex items-center mb-3">
                          <MessageSquare size={18} className="mr-2 text-[#01CC54]" />
                          <h4 className="text-sm font-semibold text-gray-900">
                            Button & Message Reply Settings
                          </h4>
                        </div>
                        <div className="space-y-4">
                          {/* Reply to All Messages */}
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                Reply to All Messages
                              </div>
                              <div className="text-xs text-gray-600 mt-0.5">
                                AI will reply to all messages (both button clicks and text messages)
                              </div>
                            </div>
                            <Switch
                              enabled={aiReplyToAll}
                              onChange={handleSaveAiReplyToAll}
                            />
                          </div>

                          {/* Reply to Button Messages */}
                          <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900">
                                Reply to Button Messages
                              </div>
                              <div className="text-xs text-gray-600 mt-0.5">
                                AI will reply when customers click buttons. Leave button texts empty to reply to all buttons.
                              </div>
                            </div>
                            <Switch
                              enabled={aiReplyToButtons}
                              onChange={handleSaveAiReplyToButtons}
                              disabled={aiReplyToAll}
                            />
                          </div>

                          {/* Specific Button Texts - Only show when Reply to Button Messages is enabled */}
                          {aiReplyToButtons && (
                            <div className="p-3 bg-white rounded-lg border border-gray-200">
                              <AiChatbotButtonInputs
                                value={buttonTextsArray}
                                onChange={(buttons) => onAiButtonTextsChange(buttons)}
                                onSave={handleSaveAiButtonTexts}
                                label="Specific Button Texts"
                                placeholder="Enter button text..."
                                helpText="Add button texts that should trigger AI replies. If empty, AI replies to all buttons."
                              />
                            </div>
                          )}

                        </div>
                      </div>

                      {/* AI Reply Schedule */}
                      <AiChatbotScheduleField
                        aiReplyDays={aiReplyDays}
                        aiReplyStartTime={aiReplyStartTime}
                        aiReplyEndTime={aiReplyEndTime}
                        aiTimezone={aiTimezone}
                        onSave={async (data) => {
                          onScheduleChange(data);
                          await onSaveAll({
                            ai_reply_days: data.ai_reply_days,
                            ai_reply_start_time: data.ai_reply_start_time,
                            ai_reply_end_time: data.ai_reply_end_time,
                            ai_timezone: data.ai_timezone,
                          });
                        }}
                      />
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
      <CreditPurchaseModal
        isOpen={showCreditModal}
        onClose={() => setShowCreditModal(false)}
        onSuccess={() => {
          loadUserCredits();
        }}
      />
    </AnimatePresence>
  );
};

export default AiChatbotModal;
